import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Paper,
  ButtonGroup,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import SpaIcon from '@mui/icons-material/Spa';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { authaltFetch, AUTHALT_API_BASE } from '../services/authaltApi';

// API Configuration - token fetched dynamically from backend
const GUEST_API_BASE = AUTHALT_API_BASE;

// Atmantan brand colors
const ATMANTAN_GOLD = '#C7AE6E';
const ATMANTAN_DARK_GOLD = '#B09858';

const StandaloneItinerary = () => {
  // Get room number from URL query parameters
  const queryParams = new URLSearchParams(window.location.search);
  const roomNo = queryParams.get('room');

  const [guestsInRoom, setGuestsInRoom] = useState([]);
  const [selectedGuestIndex, setSelectedGuestIndex] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [guestInfo, setGuestInfo] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [roomsData, setRoomsData] = useState({});

  // Fetch rooms configuration
  const fetchRoomsData = async () => {
    try {
      const response = await apiGet('/api/config');
      if (response.ok) {
        const data = await response.json();
        setRoomsData(data.rooms || {});
      }
    } catch (error) {
      console.error('Error fetching rooms data:', error);
    }
  };

  // Check if guest is currently in house
  const isGuestInHouse = (guest) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    
    const checkIn = guest.checkIn || guest.startDate;
    const checkOut = guest.checkOut || guest.endDate;
    
    if (!checkIn || !checkOut) return false;
    
    // Parse dates - handle both YYYY-MM-DD and DD-MM-YYYY formats
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      // Try ISO format first
      let date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        // Try DD-MM-YYYY format
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          date = new Date(parts[2], parts[1] - 1, parts[0]);
        }
      }
      date.setHours(0, 0, 0, 0);
      return date;
    };
    
    const checkInDate = parseDate(checkIn);
    const checkOutDate = parseDate(checkOut);
    
    if (!checkInDate || !checkOutDate) return false;
    
    // Guest is in house if today is between check-in date and check-out date (inclusive)
    return today >= checkInDate && today <= checkOutDate;
  };

  // Fetch guests by room number
  const fetchGuestsByRoom = async () => {
    if (!roomNo) return;
    
    try {
      setLoading(true);
      // Use InHouse type to get currently checked-in guests
      const response = await authaltFetch('/guestlistdatav3', {
        type: 'InHouse',
        index: 'atmantan'
      });
      if (response.ok) {
        const result = await response.json();
        const data = result.inHouse?.data || [];
        // Filter by room number and only show guests currently in house
        const guests = (Array.isArray(data) ? data : []).filter(g => {
          const matchesRoom = (g.room || g.roomNo || g.location || '').toString() === roomNo.toString();
          return matchesRoom && isGuestInHouse(g);
        });
        
        setGuestsInRoom(guests);
        
        // Auto-select first guest
        if (guests.length > 0) {
          setSelectedGuestIndex(0);
          setGuestInfoFromGuest(guests[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching guests:', error);
    } finally {
      setLoading(false);
    }
  };

  const setGuestInfoFromGuest = (guest) => {
    setGuestInfo({
      guestId: guest.guestId || guest.id || '',
      guestName: guest.guestName || guest.guest_name || `${guest.firstName || ''} ${guest.lastName || ''}`.trim(),
      room: guest.room || guest.roomNo || guest.location || '',
      consultingDoctor: guest.consultingDoctor || guest.doctor || '',
      retreatName: guest.package || guest.program || 'Wellness Retreat',
      checkIn: guest.checkIn || guest.startDate || '',
      checkOut: guest.checkOut || guest.endDate || ''
    });
  };

  const handleTabChange = (event, newValue) => {
    setSelectedGuestIndex(newValue);
    setGuestInfoFromGuest(guestsInRoom[newValue]);
  };

  // Fetch itinerary
  const fetchItinerary = async () => {
    if (!guestsInRoom || guestsInRoom.length === 0) return;
    
    const selectedGuest = guestsInRoom[selectedGuestIndex];
    if (!selectedGuest) return;
    
    const guestId = selectedGuest.guestId || selectedGuest.id;
    
    try {
      setLoading(true);
      const allBookings = [];
      
      for (const date of dateRange) {
        const response = await apiGet(`/api/bookings/${date}`);
        if (response.ok) {
          const data = await response.json();
          const guestBookings = (Array.isArray(data) ? data : []).filter(b => 
            b.guestId === guestId || b.guest_id === guestId
          );
          
          guestBookings.forEach(b => {
            b.date = date;
            if (b.room && roomsData[b.room]) {
              b.location = roomsData[b.room].location || '';
            }
          });
          
          allBookings.push(...guestBookings);
        }
      }
      
      allBookings.sort((a, b) => {
        if (a.date !== b.date) {
          return a.date.localeCompare(b.date);
        }
        return (a.startTime || '').localeCompare(b.startTime || '');
      });
      
      setBookings(allBookings);
    } catch (error) {
      console.error('Error fetching itinerary:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize date range - only today and tomorrow
  useEffect(() => {
    const today = new Date();
    const days = [];
    // Only show today (0) and tomorrow (1)
    for (let i = 0; i <= 1; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      days.push(d.toISOString().slice(0, 10));
    }
    setDateRange(days);
    fetchRoomsData();
  }, []);

  // Fetch guests when room number is available
  useEffect(() => {
    if (roomNo) {
      fetchGuestsByRoom();
    }
  }, [roomNo]);

  // Fetch itinerary when dependencies are ready
  useEffect(() => {
    if (guestsInRoom.length > 0 && Object.keys(roomsData).length > 0 && dateRange.length > 0) {
      fetchItinerary();
    }
  }, [selectedGuestIndex, guestsInRoom, roomsData, dateRange]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const formatTimeRange = (startTime, endTime) => {
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const getActivityIcon = (treatmentName) => {
    const name = (treatmentName || '').toLowerCase();
    const iconStyle = { fontSize: 20, color: ATMANTAN_GOLD };
    
    if (name.includes('breakfast') || name.includes('wake up')) {
      return <FreeBreakfastIcon sx={iconStyle} />;
    } else if (name.includes('lunch') || name.includes('dinner') || name.includes('mid morning') || name.includes('evening')) {
      return <RestaurantIcon sx={iconStyle} />;
    } else if (name.includes('fitness') || name.includes('gym') || name.includes('training')) {
      return <FitnessCenterIcon sx={iconStyle} />;
    } else if (name.includes('yoga') || name.includes('meditation') || name.includes('consultation')) {
      return <SelfImprovementIcon sx={iconStyle} />;
    } else {
      return <SpaIcon sx={iconStyle} />;
    }
  };

  // Group bookings by date and add standard items
  const bookingsByDate = useMemo(() => {
    const grouped = {};
    
    const standardItems = [
      { startTime: '06:30', endTime: '07:00', treatment: 'KRIYA YOGA', location: 'Yoga Hall', room: 'Yoga Hall', isStandard: true },
      { startTime: '07:00', endTime: '09:00', treatment: 'BREAKFAST', location: 'Restaurant', room: 'Restaurant', isStandard: true },
      { startTime: '12:00', endTime: '14:30', treatment: 'LUNCH', location: 'Restaurant', room: 'Restaurant', isStandard: true },
      { startTime: '19:00', endTime: '21:00', treatment: 'DINNER', location: 'Restaurant', room: 'Restaurant', isStandard: true }
    ];
    
    bookings.forEach(booking => {
      const date = booking.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(booking);
    });
    
    Object.keys(grouped).forEach(date => {
      standardItems.forEach(item => {
        grouped[date].push({ ...item, date });
      });
      
      // Sort by start time (convert to minutes for proper chronological order)
      grouped[date].sort((a, b) => {
        const timeA = a.startTime || '99:99';
        const timeB = b.startTime || '99:99';
        
        // Convert HH:MM to minutes for accurate comparison
        const getMinutes = (time) => {
          const [hours, mins] = time.split(':').map(Number);
          return hours * 60 + mins;
        };
        
        return getMinutes(timeA) - getMinutes(timeB);
      });
    });
    
    return grouped;
  }, [bookings]);

  const getDayNumber = (dateStr) => {
    if (!guestInfo?.checkIn) return null;
    const checkIn = new Date(guestInfo.checkIn);
    const currentDate = new Date(dateStr);
    const diffTime = currentDate - checkIn;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  const availableDays = useMemo(() => {
    return Object.keys(bookingsByDate).map(date => ({
      date,
      dayNum: getDayNumber(date)
    })).filter(d => d.dayNum).sort((a, b) => a.dayNum - b.dayNum);
  }, [bookingsByDate, guestInfo]);

  // Render list view
  const renderListView = () => {
    const datesToShow = Object.keys(bookingsByDate);

    if (datesToShow.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            No bookings found for this selection
          </Typography>
        </Box>
      );
    }
    
    return (
      <>
        {datesToShow.map((date) => {
          const dayBookings = bookingsByDate[date];
          const dayNum = getDayNumber(date);
          
          return (
            <Box key={date} sx={{ mb: 4 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                pb: 1,
                borderBottom: `2px solid ${ATMANTAN_GOLD}`
              }}>
                <Box>
                  {dayNum && <Typography variant="h6" sx={{ fontWeight: 600 }}>Day {dayNum}</Typography>}
                  <Typography variant="body2" color="text.secondary">{formatDate(date)}</Typography>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Treatment</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dayBookings.map((booking, idx) => (
                      <TableRow key={idx} sx={{ '&:hover': { backgroundColor: `${ATMANTAN_GOLD}10` } }}>
                        <TableCell sx={{ py: 1.5, fontSize: '0.85rem', fontFamily: 'monospace' }}>
                          {formatTimeRange(booking.startTime, booking.endTime)}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                            {booking.treatment}
                          </Typography>
                          {!booking.isStandard && (
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              Orientation: 10 mins before
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, fontSize: '0.85rem' }}>
                          {booking.location}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
      </>
    );
  };

  // Render card view
  const renderCardView = () => {
    const datesToShow = Object.keys(bookingsByDate);

    if (datesToShow.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            No bookings found for this selection
          </Typography>
        </Box>
      );
    }
    
    return (
      <>
        {datesToShow.map((date) => {
          const dayBookings = bookingsByDate[date];
          const dayNum = getDayNumber(date);
          
          return (
            <Box key={date} sx={{ mb: 4 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                pb: 1,
                borderBottom: `2px solid ${ATMANTAN_GOLD}`
              }}>
                <Box>
                  {dayNum && <Typography variant="h6" sx={{ fontWeight: 600 }}>Day {dayNum}</Typography>}
                  <Typography variant="body2" color="text.secondary">{formatDate(date)}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dayBookings.map((booking, idx) => (
                  <Box 
                    key={idx}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '40px 300px 1fr',
                      gap: 2,
                      p: 2,
                      borderRadius: 1,
                      border: `1px solid ${ATMANTAN_GOLD}40`,
                      '&:hover': { backgroundColor: `${ATMANTAN_GOLD}10` }
                    }}
                  >
                    <Box sx={{ flexShrink: 0 }}>
                      {getActivityIcon(booking.treatment)}
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'text.secondary' }}>
                        {formatTimeRange(booking.startTime, booking.endTime)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mt: 0.2 }}>
                        {booking.treatment}
                      </Typography>
                      {!booking.isStandard && (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', mt: 0.2 }}>
                          Orientation: 10 mins before
                        </Typography>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Location
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {booking.location}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </>
    );
  };

  if (!roomNo) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            No Room Number Provided
          </Typography>
          <Typography color="text.secondary">
            Please add ?room=ROOM_NUMBER to the URL
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (loading && guestsInRoom.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: ATMANTAN_GOLD }} />
        </Box>
      </Container>
    );
  }

  if (!loading && guestsInRoom.length === 0 && roomNo) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ color: ATMANTAN_DARK_GOLD }}>
            No Current Guests in Room {roomNo}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            There are no guests currently checked in to this room.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Only guests who are currently in-house (between check-in and check-out dates) are shown.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Guest Itinerary
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Room {roomNo}
            </Typography>
          </Box>
          <img src="/Atmantan.png" alt="Atmantan" style={{ height: 50 }} />
        </Box>

        {guestsInRoom.length > 1 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
            <Tabs 
              value={selectedGuestIndex} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '1rem'
                },
                '& .Mui-selected': {
                  color: ATMANTAN_DARK_GOLD
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: ATMANTAN_GOLD
                }
              }}
            >
              {guestsInRoom.map((guest, index) => (
                <Tab 
                  key={guest.guestId || guest.id || index} 
                  label={guest.guestName || guest.guest_name || `${guest.firstName || ''} ${guest.lastName || ''}`.trim()}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Check-in/Check-out Info */}
        {guestInfo && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {guestInfo.checkIn && (
              <Chip 
                label={`Check-in: ${formatDate(guestInfo.checkIn)}`}
                sx={{ 
                  backgroundColor: `${ATMANTAN_GOLD}20`,
                  color: ATMANTAN_DARK_GOLD,
                  fontWeight: 500
                }}
              />
            )}
            {guestInfo.checkOut && (
              <Chip 
                label={`Check-out: ${formatDate(guestInfo.checkOut)}`}
                sx={{ 
                  backgroundColor: `${ATMANTAN_GOLD}20`,
                  color: ATMANTAN_DARK_GOLD,
                  fontWeight: 500
                }}
              />
            )}
          </Box>
        )}
      </Paper>

      {guestsInRoom.length > 0 && guestInfo && (
        <>
          {/* View Controls */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <ButtonGroup size="small" variant="outlined">
                <Button
                  onClick={() => setViewMode('list')}
                  variant={viewMode === 'list' ? 'contained' : 'outlined'}
                  startIcon={<ViewListIcon />}
                  sx={{
                    backgroundColor: viewMode === 'list' ? ATMANTAN_GOLD : 'transparent',
                    color: viewMode === 'list' ? '#171717' : '#666',
                    borderColor: ATMANTAN_GOLD,
                    '&:hover': {
                      backgroundColor: viewMode === 'list' ? ATMANTAN_DARK_GOLD : 'rgba(199, 174, 110, 0.08)',
                      borderColor: ATMANTAN_GOLD
                    }
                  }}
                >
                  List
                </Button>
                <Button
                  onClick={() => setViewMode('card')}
                  variant={viewMode === 'card' ? 'contained' : 'outlined'}
                  startIcon={<ViewModuleIcon />}
                  sx={{
                    backgroundColor: viewMode === 'card' ? ATMANTAN_GOLD : 'transparent',
                    color: viewMode === 'card' ? '#171717' : '#666',
                    borderColor: ATMANTAN_GOLD,
                    '&:hover': {
                      backgroundColor: viewMode === 'card' ? ATMANTAN_DARK_GOLD : 'rgba(199, 174, 110, 0.08)',
                      borderColor: ATMANTAN_GOLD
                    }
                  }}
                >
                  Cards
                </Button>
              </ButtonGroup>
            </Box>
          </Paper>

          {/* Itinerary Content */}
          <Paper sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: ATMANTAN_GOLD }} />
              </Box>
            ) : (
              viewMode === 'list' ? renderListView() : renderCardView()
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};

export default StandaloneItinerary;

